import React, { useState } from 'react';
import { Row, Col, Card, Statistic, Table, Button, Modal, Typography, Space, Tag, Divider } from 'antd';
import {
    LineChartOutlined,
    TrophyOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    EyeOutlined
} from '@ant-design/icons';
import {
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import './NicheAnalysis.css';
import {
    nicheData,
    COLORS,
    getComparisonStats,
    getTopListings,
    getTopSkus,
    getTopHashtags,
    generateLineChartData
} from './mockData';

const { Title, Text } = Typography;

const NicheAnalysis = () => {
    const [selectedNiche, setSelectedNiche] = useState(null);
    const [isChartModalVisible, setIsChartModalVisible] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [chartTitle, setChartTitle] = useState('');

    const handlePieClick = (data) => {
        setSelectedNiche(data.type);
    };

    const handleChartView = (title, type, record) => {
        const data = generateLineChartData(type);
        setChartData(data);
        setChartTitle(`${title} - ${record.sku || record.tag}`);
        setIsChartModalVisible(true);
    };

    const renderComparisonValue = (value, isPositive) => (
        <span className={isPositive ? 'comparison-positive' : 'comparison-negative'}>
            {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            {Math.abs(value)}%
        </span>
    );

    const listingsColumns = [
        {
            title: 'SKU/ASIN',
            dataIndex: 'sku',
            key: 'sku',
            ellipsis: true,
            render: (sku, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <img
                        src="https://m.media-amazon.com/images/I/81BhE8Jq9NL.jpg"
                        alt="Product mockup"
                        style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            flexShrink: 0
                        }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', color: '#333', marginBottom: '2px' }} title={record.title}>
                            {record.title}
                        </div>
                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{sku}</div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                            <a href={`https://amazon.com/dp/${record.asin}`} target="_blank" rel="noopener noreferrer">
                                {record.asin}
                            </a>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: 'Sales',
            dataIndex: 'currentSales',
            key: 'currentSales',
            align: 'center',
            width: 70,
            render: (sales, record) => (
                <strong
                    style={{
                        color: '#1890ff',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                    }}
                    onClick={() => handleChartView('Sales Trend', 'sales', record)}
                    title="Click to view sales chart"
                >
                    {sales}
                </strong>
            )
        },
        {
            title: <span><span>vs</span> Month</span>,
            dataIndex: 'monthComparison',
            key: 'monthComparison',
            align: 'center',
            width: 80,
            render: (value) => renderComparisonValue(value, value > 0)
        },
        {
            title: 'vs Year',
            dataIndex: 'yearComparison',
            key: 'yearComparison',
            align: 'center',
            width: 80,
            render: (value) => renderComparisonValue(value, value > 0)
        },
        {
            title: 'Rank',
            dataIndex: 'rank',
            key: 'rank',
            align: 'center',
            width: 80,
            render: (rank, record) => (
                <span
                    style={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        color: '#333'
                    }}
                    onClick={() => handleChartView('Rank Trend', 'rank', record)}
                    title="Click to view rank chart"
                >
                    {`#${rank.toLocaleString()}`}
                </span>
            )
        }
    ];

    const skuColumns = [
        {
            title: 'SKU/ASIN',
            dataIndex: 'sku',
            key: 'sku',
            ellipsis: true,
            render: (sku, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <img
                        src="https://m.media-amazon.com/images/I/81BhE8Jq9NL.jpg"
                        alt="Product mockup"
                        style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            flexShrink: 0
                        }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', color: '#333', marginBottom: '2px' }} title={record.title}>
                            {record.title}
                        </div>
                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{`${sku}-${record.color}-${record.size}`}</div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                            <a href={`https://amazon.com/dp/${record.asin}`} target="_blank" rel="noopener noreferrer">
                                {record.asin}
                            </a>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: 'Sales',
            dataIndex: 'currentSales',
            key: 'currentSales',
            align: 'center',
            width: 70,
            render: (sales, record) => (
                <strong
                    style={{
                        color: '#1890ff',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                    }}
                    onClick={() => handleChartView('Sales Trend', 'sales', record)}
                    title="Click to view sales chart"
                >
                    {sales}
                </strong>
            )
        },
        {
            title: 'vs Month',
            dataIndex: 'monthComparison',
            key: 'monthComparison',
            align: 'center',
            width: 80,
            render: (value) => renderComparisonValue(value, value > 0)
        },
        {
            title: 'vs Year',
            dataIndex: 'yearComparison',
            key: 'yearComparison',
            align: 'center',
            width: 80,
            render: (value) => renderComparisonValue(value, value > 0)
        },
        {
            title: 'Rank',
            dataIndex: 'rank',
            key: 'rank',
            align: 'center',
            width: 80,
            render: (rank, record) => (
                <span
                    style={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        color: '#333'
                    }}
                    onClick={() => handleChartView('Rank Trend', 'rank', record)}
                    title="Click to view rank chart"
                >
                    {`#${rank.toLocaleString()}`}
                </span>
            )
        }
    ];

    // Custom label function for pie chart with prominent percentage
    const renderCustomLabel = (entry) => {
        const { type, percentage } = entry;
        // Show only percentage in large, bold text
        return `${percentage}%`;
    };

    // Custom label render component for better styling
    const CustomLabel = (props) => {
        const { cx, cy, midAngle, innerRadius, outerRadius, percentage, index } = props;
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.6; // Thụt vào trong hơn
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text 
                x={x} 
                y={y} 
                fill="#ffffff" 
                textAnchor="middle" 
                dominantBaseline="central"
                fontSize="14"
                fontWeight="800"
                stroke="#000000"
                strokeWidth="0.3"
            >
                {`${percentage}%`}
            </text>
        );
    };

    const comparisonStats = selectedNiche ? getComparisonStats(selectedNiche) : null;
    const topListings = selectedNiche ? getTopListings(selectedNiche) : [];
    const topSkus = selectedNiche ? getTopSkus(selectedNiche) : [];
    const topHashtags = selectedNiche ? getTopHashtags(selectedNiche) : [];

    return (
        <div className="niche-analysis">
            {/* Main Content Grid */}
            <div className="analysis-grid">
                {/* Left Panel - Control Panel */}
                <div className="chart-panel">
                    <div>
                        <Title level={4} style={{ color: '#1e293b', margin: 0 }}>
                            Control Panel
                        </Title>
                        <Text style={{ color: '#64748b' }}>
                            Select niche segments for analysis
                        </Text>
                    </div>
                    <div className="chart-container">
                        <ResponsiveContainer width="90%" height={300}>
                            <PieChart>
                                <Pie
                                    data={nicheData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={120}
                                    dataKey="value"
                                    onClick={(data) => handlePieClick(data)}
                                    label={CustomLabel}
                                    labelLine={false}
                                >
                                    {nicheData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                               
                                <Tooltip
                                    formatter={(value, name, props) => [
                                        `${value} units (${props.payload.percentage}%)`,
                                        props.payload.type
                                    ]}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Niche Selection Controls */}
                    <div>
                        <Title level={5} style={{ margin: '16px 0 8px 0', color: '#1e293b' }}>
                            Niche Categories
                        </Title>
                        <div className="niche-cards-grid">
                            {nicheData.map((niche, index) => (
                                <Card
                                    key={index}
                                    className={`niche-card ${selectedNiche === niche.type ? 'selected' : ''}`}
                                    size="small"
                                    onClick={() => handlePieClick(niche)}
                                    hoverable
                                >
                                    <div className="niche-indicator" style={{ backgroundColor: COLORS[index] }}></div>
                                    <div className="niche-info">
                                        <Text strong className="niche-name">{niche.type}</Text>
                                        <div className="niche-stats">
                                            <span className="niche-value">{niche.value}</span>
                                            <span className="niche-percentage">{niche.percentage}%</span>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Panel - Results Display */}
                <div className="details-panel">
                    <div>
                        <Title level={4} style={{ color: '#1e293b', margin: 0 }}>
                            {selectedNiche ? `Analysis Results - "${selectedNiche}"` : 'Analysis Results'}
                        </Title>
                        <Text style={{ color: '#64748b' }}>
                            {selectedNiche ? 'Comprehensive performance analysis and insights' : 'Detailed insights and performance metrics'}
                        </Text>
                    </div>
                    <Divider dashed style={{ margin: '10px 0' }} />
                    {selectedNiche ? (
                        <div className="niche-details">

                            {/* Metrics and Trend Layout - 2 Columns */}
                            <Row gutter={12} style={{ marginBottom: 16 }}>
                                {/* Left Column: Metrics Cards */}
                                <Col xs={24} lg={6}>
                                    <div className="metrics-column">
                                        <Card className="metric-card" size="small">
                                            <Statistic
                                                title="Total Sales"
                                                value={comparisonStats.totalSales}
                                                suffix="units"
                                                valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                                            />
                                        </Card>
                                        <Card className="metric-card" size="small">
                                            <Statistic
                                                title="vs Last Month"
                                                value={Math.abs(comparisonStats.monthComparison.value)}
                                                suffix="%"
                                                prefix={comparisonStats.monthComparison.isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                                valueStyle={{
                                                    color: comparisonStats.monthComparison.isPositive ? '#10b981' : '#ef4444',
                                                    fontSize: '20px'
                                                }}
                                            />
                                        </Card>
                                        <Card className="metric-card" size="small">
                                            <Statistic
                                                title="vs Last Year"
                                                value={Math.abs(comparisonStats.yearComparison.value)}
                                                suffix="%"
                                                prefix={comparisonStats.yearComparison.isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                                valueStyle={{
                                                    color: comparisonStats.yearComparison.isPositive ? '#10b981' : '#ef4444',
                                                    fontSize: '20px'
                                                }}
                                            />
                                        </Card>
                                    </div>
                                </Col>

                                {/* Right Column: Trend Chart */}
                                <Col xs={24} lg={18}>
                                    <Card className="trend-card" title="30-Day Sales Trend" size="small">
                                <ResponsiveContainer width="100%" height={180}>
                                    <LineChart data={generateLineChartData('sales')}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={(date) => {
                                                const d = new Date(date);
                                                return `${d.getMonth() + 1}/${d.getDate()}`;
                                            }}
                                            fontSize={11}
                                        />
                                        <YAxis fontSize={11} />
                                        <Tooltip
                                            labelFormatter={(date) => {
                                                const d = new Date(date);
                                                return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
                                            }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke="#1890ff"
                                            strokeWidth={2}
                                            dot={{ r: 2 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Top Listings and SKUs - Side by Side */}
                            <Row gutter={16} style={{ marginTop: 5 }}>
                                <Col xs={24} lg={12}>
                                    <div style={{ marginBottom: 10 }}><b>Top Selling Listings</b></div>
                                    <Table
                                        className="niche-table"
                                        columns={listingsColumns}
                                        dataSource={topListings}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: false,
                                            showQuickJumper: false,
                                            size: 'small'
                                        }}
                                        size="small"
                                    />
                                </Col>
                                <Col xs={24} lg={12}>
                                    <div style={{ marginBottom: 10 }}><b>Top SKU Variants</b></div>
                                    <Table
                                        className="niche-table"
                                        columns={skuColumns}
                                        dataSource={topSkus}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: false,
                                            showQuickJumper: false,
                                            size: 'small'
                                        }}
                                        size="small"
                                    />
                                </Col>
                            </Row>

                            {/* Top Hashtags */}
                            <Card className="hashtags-card" title="Top Performing Quotes" size="small">
                                <div className="quotes-list">
                                    {topHashtags.map((hashtag, index) => (
                                        <div key={index} className="quote-item" style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'flex-start',
                                            padding: '8px 0',
                                            borderBottom: index < topHashtags.length - 1 ? '1px solid #f0f0f0' : 'none'
                                        }}>
                                            <div style={{ 
                                                flex: 1, 
                                                marginRight: '12px',
                                                lineHeight: '1.4'
                                            }}>
                                                <Text style={{ 
                                                    fontStyle: 'italic',
                                                    color: '#333',
                                                    fontSize: '13px'
                                                }}>
                                                    "{hashtag.tag}"
                                                </Text>
                                            </div>
                                            <div style={{ 
                                                flexShrink: 0,
                                                background: '#e6f7ff',
                                                color: '#1890ff',
                                                padding: '2px 8px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: 'bold'
                                            }}>
                                                {hashtag.totalSales.toLocaleString()}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </div>
                    ) : (
                        <Card className="placeholder-card">
                            <div className="placeholder-content">
                                <LineChartOutlined className="placeholder-icon" />
                                <Title level={4} className="placeholder-title">
                                    No Niche Selected
                                </Title>
                                <Text className="placeholder-text">
                                    Please select a niche segment from the control panel on the left to view detailed analysis, performance metrics, and comprehensive insights.
                                </Text>
                            </div>
                        </Card>
                    )}
                </div>
            </div>

            {/* Chart Modal */}
            <Modal
                title={chartTitle}
                open={isChartModalVisible}
                onCancel={() => setIsChartModalVisible(false)}
                footer={null}
                width={800}
                destroyOnClose
            >
                {chartData.length > 0 && (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tickFormatter={(date) => {
                                    const d = new Date(date);
                                    return `${d.getMonth() + 1}/${d.getDate()}`;
                                }}
                            />
                            <YAxis
                                tickFormatter={(value) => {
                                    if (chartTitle.includes('Rank')) {
                                        return `#${parseInt(value).toLocaleString()}`;
                                    }
                                    return value;
                                }}
                            />
                            <Tooltip
                                labelFormatter={(date) => {
                                    const d = new Date(date);
                                    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
                                }}
                                formatter={(value) => {
                                    const name = chartTitle.includes('Sales') ? 'Sales' : 'Rank';
                                    const displayValue = chartTitle.includes('Rank') ? `#${value}` : value;
                                    return [displayValue, name];
                                }}
                            />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#1890ff"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Modal>
        </div>
    );
};

export default NicheAnalysis;